import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext, KeyboardSensor, MeasuringStrategy, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AlertTriangle, Check, ChevronsDownUp, ChevronsUpDown, Lightbulb, ListChecks, Plus } from 'lucide-react'
import { liveApi, type LiveQuestion } from '@/api/live'
import { Button } from '@/components/ui/button'
import { blankQuestion, newKey } from './QuestionEditor'
import { ActivityRow, BuiltInFeedbackRow } from './ActivityRow'
import { AddActivityDialog } from './AddActivityDialog'
import { STARTER_SET, entryById, type CatalogueEntry } from './liveActivityCatalogue'
import {
  MAX_ACTIVITIES, blank, copyQuestions, fromDraft, problemWith, toDraft, type Draft,
} from './liveActivityDraft'

/** A new activity from a catalogue entry, filled in sensibly: feedback gets the built-in questions, a post-course quiz
 *  or an "after" check starts with a copy of its pre-course or "before" counterpart so the two ask the same thing. */
function buildFromEntry(entry: CatalogueEntry, existing: Draft[], defaultFeedback: LiveQuestion[]): Draft {
  if (entry.id === 'feedback') return blank('quiz', entry.name, 'feedback', defaultFeedback.map(q => ({ ...q, id: null })))

  const draft = blank(entry.type, entry.name, entry.role)
  if (entry.starterQuestion) draft.questions = [blankQuestion(entry.starterQuestion)]
  const counterpart = entry.role === 'post' ? 'pre' : entry.role === 'after' ? 'before' : null
  if (counterpart) {
    const other = existing.find(d => d.type === entry.type && d.role === counterpart && d.questions.length > 0)
    if (other) draft.questions = copyQuestions(other)
  }
  return draft
}

/**
 * The course's prepared CertSlate Live activities, in the order the trainer will see them. They're snapshotted
 * when a live session starts, so editing here never changes a session that's already running.
 */
export function CourseLiveActivitiesTab({ courseId, onDirtyChange }: {
  courseId: string
  /** Told whenever there are unsaved changes (and false again when this tab goes away), so the page can warn before switching tabs. */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['live-activities', courseId],
    queryFn: () => liveApi.getCourseActivities(courseId).then(r => r.data),
  })

  const [drafts, setDrafts] = useState<Draft[]>([])
  const [open, setOpen] = useState<Set<string>>(new Set())      // keys of the rows showing their editor
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [flash, setFlash] = useState(false)
  const scrollTo = useRef<string | null>(null)

  // Re-sync when the server's copy changes (after a save, or when another tab edited it).
  useEffect(() => { if (data) setDrafts(data.activities.map(toDraft)) }, [data])

  // After adding something (or jumping to a problem), bring that row into view.
  useEffect(() => {
    if (!scrollTo.current) return
    const el = document.getElementById(`activity-${scrollTo.current}`)
    scrollTo.current = null
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [drafts])

  // One toggle for the lot: it offers to collapse when everything is open, and to expand otherwise.
  const allOpen = drafts.length > 0 && drafts.every(d => open.has(d.key))

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const wire = useMemo(() => drafts.map(fromDraft), [drafts])
  const dirty = useMemo(() => !!data && JSON.stringify(wire) !== JSON.stringify(data.activities), [wire, data])

  // Unsaved work is easy to lose here, so every way out asks first: closing or refreshing the tab, navigating to another
  // page, and (through onDirtyChange) switching to another tab on the course page.
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm('You have unsaved live activity changes. Leave without saving?')) blocker.proceed()
    else blocker.reset()
  }, [blocker])

  const problems = drafts.map(a => problemWith(a, drafts))
  const problemCount = problems.filter(Boolean).length
  const hasProblem = problemCount > 0

  const save = useMutation({
    mutationFn: () => liveApi.saveCourseActivities(courseId, wire),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-activities', courseId] })
      setFlash(true)
      setTimeout(() => setFlash(false), 2000)
    },
  })

  if (isLoading || !data) return <p className="text-sm text-gray-500">Loading…</p>

  const has = (type: Draft['type'], role: Draft['role']) => drafts.some(d => d.type === type && d.role === role)
  const hasFeedback = has('quiz', 'feedback')
  const atLimit = drafts.length >= MAX_ACTIVITIES

  const update = (key: string, next: Draft) => setDrafts(ds => ds.map(d => (d.key === key ? next : d)))
  const remove = (key: string) => {
    setDrafts(ds => ds.filter(d => d.key !== key))
    setOpen(s => { const n = new Set(s); n.delete(key); return n })
  }
  const duplicate = (a: Draft) => {
    // A copy can't take the original's role (each can be used once), so it becomes a stand-alone activity.
    const copy: Draft = { ...a, id: null, key: newKey(), name: `${a.name.trim() || 'Activity'} (copy)`, role: null, questions: copyQuestions(a).map(q => ({ ...q, id: null })) }
    setDrafts(ds => { const i = ds.findIndex(d => d.key === a.key); return [...ds.slice(0, i + 1), copy, ...ds.slice(i + 1)] })
    setOpen(s => new Set(s).add(copy.key))
    scrollTo.current = copy.key
  }
  const toggle = (key: string) => setOpen(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n })

  const add = (entry: CatalogueEntry) => {
    const draft = buildFromEntry(entry, drafts, data.defaultFeedback)
    setDrafts(ds => [...ds, draft])
    setOpen(s => new Set(s).add(draft.key))     // a new activity opens, ready to fill in
    scrollTo.current = draft.key
  }

  const addStarterSet = () => {
    let next = drafts
    for (const id of STARTER_SET) {
      const entry = entryById(id)
      if (next.length >= MAX_ACTIVITIES || (entry.role && next.some(d => d.type === entry.type && d.role === entry.role))) continue
      next = [...next, buildFromEntry(entry, next, data.defaultFeedback)]
    }
    setDrafts(next)
  }

  const showProblems = () => {
    const keys = drafts.filter((_, i) => problems[i]).map(d => d.key)
    setOpen(s => new Set([...s, ...keys]))
    scrollTo.current = keys[0] ?? null
    setDrafts(ds => [...ds])                    // re-run the scroll effect
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(false)
    if (!over || active.id === over.id) return
    setDrafts(ds => arrayMove(ds, ds.findIndex(d => d.key === active.id), ds.findIndex(d => d.key === over.id)))
  }

  // Gentle suggestions when one half of a before/after pair is missing.
  const tips: { text: string; entry: CatalogueEntry }[] = []
  if (has('quiz', 'pre') && !has('quiz', 'post')) tips.push({ text: 'You have a pre-course quiz. Add a post-course quiz to show how much people learned.', entry: entryById('post-quiz') })
  if (has('quiz', 'post') && !has('quiz', 'pre')) tips.push({ text: 'You have a post-course quiz. Add a pre-course quiz to show how much people learned.', entry: entryById('pre-quiz') })
  if (has('confidence', 'before') && !has('confidence', 'after')) tips.push({ text: 'You have a “before” confidence check. Add an “after” check to show the change.', entry: entryById('confidence-after') })
  if (has('confidence', 'after') && !has('confidence', 'before')) tips.push({ text: 'You have an “after” confidence check. Add a “before” check to show the change.', entry: entryById('confidence-before') })

  const errorText = (save.error as { response?: { data?: { detail?: string; title?: string; errors?: Record<string, string[]> } } } | null)?.response?.data
  const message = errorText?.detail ?? (errorText?.errors ? Object.values(errorText.errors).flat()[0] : errorText?.title)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="text-lg font-semibold text-gray-900">Live activities</h2>
          <p className="text-sm text-gray-500 mt-1">
            Set up live activities that trainers can run during a booking or session. Attendees answer on their phones, anonymously.
            Changes apply to the next session you start, and won&apos;t take effect on any open sessions.
          </p>
        </div>
        <Button type="button" className="gap-1.5" onClick={() => setDialogOpen(true)} disabled={atLimit} title={atLimit ? `A course can have up to ${MAX_ACTIVITIES} activities` : undefined}>
          <Plus size={15} /> Add live activity
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 px-6 py-10 text-center space-y-3">
          <span className="mx-auto h-11 w-11 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center"><ListChecks size={22} /></span>
          <div>
            <p className="font-medium text-gray-900">No live activities yet</p>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              Prepare quizzes, confidence checks, word clouds and Q&amp;A for trainers to run. A before-and-after quiz plus a feedback form is a good place to start.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button type="button" onClick={addStarterSet}>Use the recommended starter set</Button>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(true)}>Browse activities</Button>
          </div>
          <p className="text-xs text-gray-400">The starter set is a pre-course quiz, a post-course quiz and a feedback form.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <p className="text-gray-500">
              <span className="font-medium text-gray-700">{drafts.length} of {MAX_ACTIVITIES}</span> · Trainers will see them in this order. Drag a card to reorder.
            </p>
            <Button
              type="button" variant="ghost" size="sm" className="gap-1.5 text-gray-600"
              onClick={() => setOpen(allOpen ? new Set() : new Set(drafts.map(d => d.key)))}
            >
              {allOpen ? <><ChevronsDownUp size={14} /> Collapse all</> : <><ChevronsUpDown size={14} /> Expand all</>}
            </Button>
          </div>

          {hasProblem && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle size={15} className="shrink-0" />
              <span className="flex-1 min-w-0">
                {problemCount} {problemCount === 1 ? 'activity needs' : 'activities need'} attention before you can save.
              </span>
              <button type="button" onClick={showProblems} className="font-medium underline cursor-pointer">Show me</button>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            // Open rows fold up when a drag starts, so the drop targets move: keep measuring them.
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={() => setDragging(true)}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDragging(false)}
          >
            <SortableContext items={drafts.map(d => d.key)} strategy={verticalListSortingStrategy}>
              <ol className="list-none p-0 m-0" aria-label="Live activities in running order">
                {drafts.map((a, i) => (
                  <ActivityRow
                    key={a.key}
                    a={a}
                    index={i}
                    last={i === drafts.length - 1 && hasFeedback}
                    open={open.has(a.key)}
                    dragging={dragging}
                    others={drafts.filter(o => o !== a)}
                    problem={problems[i]}
                    onToggle={() => toggle(a.key)}
                    onChange={next => update(a.key, next)}
                    onDelete={() => remove(a.key)}
                    onDuplicate={() => duplicate(a)}
                  />
                ))}
                {!hasFeedback && (
                  <BuiltInFeedbackRow
                    index={drafts.length}
                    questions={data.defaultFeedback.map(q => q.text)}
                    onCustomise={() => add(entryById('feedback'))}
                  />
                )}
              </ol>
            </SortableContext>
          </DndContext>

          {tips.length > 0 && (
            <ul className="space-y-2">
              {tips.map(t => (
                <li key={t.entry.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-sm text-indigo-900">
                  <Lightbulb size={15} className="shrink-0 text-indigo-500" />
                  <span className="flex-1 min-w-0">{t.text}</span>
                  <Button type="button" size="sm" variant="outline" disabled={atLimit} onClick={() => add(t.entry)}>Add {t.entry.name.toLowerCase()}</Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white/95 py-3 backdrop-blur">
        <Button type="button" disabled={!dirty || hasProblem || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {dirty && !save.isPending && (
          <Button type="button" variant="ghost" onClick={() => { setDrafts(data.activities.map(toDraft)); setOpen(new Set()) }}>Discard changes</Button>
        )}
        {flash && <span className="flex items-center gap-1 text-sm text-green-600"><Check size={14} /> Saved</span>}
        {save.isError && <span className="text-sm text-red-600">{message ?? 'Couldn’t save. Please try again.'}</span>}
        {dirty && !save.isPending && !flash && (
          <span className="text-xs text-gray-500">Unsaved changes{hasProblem ? ' — fix the highlighted activities to save' : ''}</span>
        )}
      </div>

      <AddActivityDialog open={dialogOpen} onOpenChange={setDialogOpen} drafts={drafts} onAdd={add} />
    </div>
  )
}
